"use client";

import type { ComponentProps, ReactNode } from "react";
import {
	type FieldValues,
	FormProvider,
	type SubmitHandler,
	type UseFormReturn,
	useFormContext,
} from "react-hook-form";
import { useIntl } from "react-intl";
import { z } from "zod";
import { Button, getErrorMap } from "../../../..";

interface Props<T extends FieldValues> extends Omit<ComponentProps<"form">, "onSubmit"> {
	form: UseFormReturn<T>;
	onSubmit: SubmitHandler<T>;
	wrapperClassName?: string;
}

export const Form = <T extends FieldValues>(props: Props<T>) => {
	const { form, onSubmit, children, className, wrapperClassName, ...rest } = props;
	const intl = useIntl();

	z.setErrorMap(getErrorMap(intl));

	return (
		<FormProvider {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				noValidate
				className={wrapperClassName}
				{...rest}
			>
				<fieldset className={className} disabled={form.formState.isSubmitting}>
					{children}
				</fieldset>
			</form>
		</FormProvider>
	);
};

type ButtonProps = ComponentProps<typeof Button>;

export const SubmitButton = (props: ButtonProps) => {
	const form = useFormContext();

	return (
		<Button
			variant="contained"
			type="submit"
			disabled={form.formState.isSubmitted && !!Object.values(form.formState.errors).length}
			isLoading={!!form?.formState.isSubmitting}
			{...props}
		/>
	);
};

export const ResetButton = (props: ButtonProps) => {
	const form = useFormContext();

	return (
		<Button
			variant="outlined"
			type="reset"
			isLoading={!!form?.formState.isSubmitting}
			onClick={() => form.reset()}
			{...props}
		/>
	);
};

interface HideWhenCheckedProps {
	name: string;
	value: unknown;
	children: ReactNode;
}

export function HideWhenChecked(props: HideWhenCheckedProps) {
	const form = useFormContext();
	const checked: unknown = form.watch(props.name);

	return checked === props.value ? null : props.children;
}
